import { useState, useEffect } from 'react';
import {
  TextField,
  Modal,
  Box,
  FormControl,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import IconFayven from '../../../../assets/svg/fayven_Icon.svg';
import { SubmitButton, AlertComponent } from '../../../../components';
import { createUser } from '../../../../service/UserServices';
import { GROUP_USER } from '../../../../config/appGroupConfig';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  border: '0px solid #000',
  boxShadow: 24,
  p: 4,
  '@media (max-width: 600px)': {
    width: 300,
    overflowY: 'auto',
    maxHeight: 550,
  },
  '@media (max-height: 500px)': {
    width: 300,
    overflowY: 'scroll',
    maxHeight: 380,
  },
};

// eslint-disable-next-line no-useless-escape
const expresionRegularWebSite =
  // eslint-disable-next-line no-useless-escape
  /^(http(s?):\/\/)?()+[a-zA-Z0-9\.\-\_]+(\.[a-zA-Z]{1,6})+(\/[a-zA-Z0-9\_\-\s\.\/\?\%\#\&\=]*)?$/;
const expresionRegularPassword =
  // eslint-disable-next-line no-useless-escape
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`])\S{10,30}$/;
const PASSWORD_HELPER_TEXT =
  'At least 10 characters, including an uppercase letter, a lowercase letter, a number, and a special character.';

const normalizeWebsite = (site) => {
  const trimmed = (site || '').trim();
  if (!trimmed) return trimmed;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export default function RegisterUser({ open, handleClose, onLoading, onSuccess, roleUser }) {
  const [handleErrors, setHandleErrors] = useState({
    isError: false,
    title: '',
    description: '',
  });
  useEffect(() => {}, [roleUser]);
  const getRole = (role) => {
    // eslint-disable-next-line no-console
    console.log(role);
    switch (role) {
      case GROUP_USER.VENDOR:
        return 1;
      case GROUP_USER.VENUE:
        return 2;
      default:
        return 1;
    }
  };
  const validationSchema = yup.object({
    firstname: yup.string('Enter your first name').required('First Name is required'),
    last_name: yup.string('Enter your last name').required('Last Name is required'),
    email: yup
      .string('Enter your email')
      .trim()
      .email('Enter a valid email')
      .required('Email is required'),
    website: yup
      .string('Enter your Web Site')
      .trim()
      .matches(expresionRegularWebSite, 'Web Site not valid')
      .required('Web Site is required'),
    number_phone: yup
      .string('Enter your phone number')
      .matches(/^[0-9]{10}$/, 'Enter a valid 10-digit phone number')
      .required('Phone Number is required'),
    password: yup
      .string('Enter your password')
      .matches(
        expresionRegularPassword,
        'The password must have at least 10 characters, an uppercase, lowercase numbers and a special character.',
      )
      .required('Password is required'),
    confirmPassword: yup
      .string('Confirm your password')
      .oneOf([yup.ref('password'), null], 'Passwords must match')
      .required('Please confirm your password'),
  });
  const initialValues = {
    firstname: '',
    last_name: '',
    email: '',
    number_phone: '',
    website: '',
    password: '',
    confirmPassword: '', // confirm password input field
    optedForSms: false,
  };

  const registerUser = (values) => {
    onLoading(true);
    setHandleErrors({ isError: false, title: '', description: '' });
    const becomeData = {
      name: values.firstname,
      last_name: values.last_name,
      email: values.email,
      id_cognito: '',
      created_at: new Date(),
      web_site: normalizeWebsite(values.website),
      fk_role: getRole(roleUser),
      password: values.password,
      phone_number: `+1${values.number_phone}`,
      opted_for_sms: values.optedForSms,
    };

    createUser(becomeData)
      .then((result) => {
        // eslint-disable-next-line no-console
        console.log(result);
        onLoading(false);
        onSuccess();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
        if (err?.response?.data?.statusCode === 409)
          setHandleErrors(() => ({
            ...handleErrors,
            isError: true,
            title: 'Conflicts',
            description: err.response.data.message,
          }));
        else
          setHandleErrors(() => ({
            ...handleErrors,
            isError: true,
            title: 'A problem has occurred',
            description: 'Try again later if the problem persists, contact us',
          }));
        onLoading(false);
      });
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={style}>
          <Grid container justifyContent="center">
            <img src={IconFayven} alt="iconlogo" />
            <Grid item xs={12} m={1} />
            {roleUser === GROUP_USER.VENDOR ? (
              <div className="title-register-user">Vendor</div>
            ) : (
              <div className="title-register-user">Venue</div>
            )}
          </Grid>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={registerUser}>
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <Form style={{ width: '100%' }} onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  size="small"
                  name="firstname"
                  label="First Name"
                  id="fullWidth"
                  value={values.firstname}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ marginTop: '10px' }}
                  error={touched.firstname && Boolean(errors.firstname)}
                  helperText={touched.firstname && errors.firstname}
                />
                <TextField
                  fullWidth
                  size="small"
                  name="last_name"
                  label="Last Name"
                  id="fullWidth"
                  value={values.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ marginTop: '10px' }}
                  error={touched.last_name && Boolean(errors.last_name)}
                  helperText={touched.last_name && errors.last_name}
                />
                <TextField
                  fullWidth
                  size="small"
                  name="email"
                  label="Email"
                  id="fullWidth"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ marginTop: '10px' }}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
                <PhoneInput
                  country="us"
                  name="number_phone"
                  value={values.number_phone}
                  onChange={(phone) => setFieldValue('number_phone', phone)}
                  onBlur={handleBlur}
                  onlyCountries={['us']}
                  placeholder="Phone Number"
                  disableCountryCode="true"
                  disableDropdown
                  inputClass="register-phone-input"
                  containerClass="register-phone-container"
                  isValid={!(touched.number_phone && Boolean(errors.number_phone))}
                />
                {touched.number_phone && errors.number_phone && (
                  <div className="input-feedback">{errors.number_phone}</div>
                )}
                <TextField
                  fullWidth
                  size="small"
                  name="website"
                  label="Web Site"
                  id="fullWidth"
                  value={values.website}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ marginTop: '10px' }}
                  error={touched.website && Boolean(errors.website)}
                  helperText={touched.website && errors.website}
                />
                <TextField
                  fullWidth
                  name="password"
                  label="Password"
                  size="small"
                  id="fullWidth"
                  type="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ marginTop: '10px' }}
                  error={touched.password && Boolean(errors.password)}
                  helperText={
                    touched.password && errors.password ? errors.password : PASSWORD_HELPER_TEXT
                  }
                />
                <TextField
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  size="small"
                  id="fullWidth"
                  type="password"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ marginTop: '10px' }}
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                />
                <FormGroup>
                  <FormControlLabel
                    label={
                      <Box mt={3}>
                        <Typography>Opt in for SMS text messages for fayVen alerts</Typography>
                        <small>Charges may apply from your service provider</small>
                      </Box>
                    }
                    control={
                      <Checkbox
                        checked={values.optedForSms}
                        fullWidth
                        name="optedForSms"
                        size="small"
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    }
                  />
                </FormGroup>
                <Grid container justifyContent="center" marginTop={2} marginBottom={1}>
                  <SubmitButton className="button-black">Create Account</SubmitButton>
                </Grid>
              </Form>
            )}
          </Formik>

          {handleErrors.isError ? (
            <AlertComponent
              title={handleErrors.title}
              content={handleErrors.description}
              severity="error"
            />
          ) : null}
        </Box>
      </Modal>
    </div>
  );
}

RegisterUser.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onLoading: PropTypes.func.isRequired,
  roleUser: PropTypes.string.isRequired,
};
